/**
 * Web-laag brug tussen de domein-overlay-engine (`evaluateOverlay`) en de
 * canvas-render. Bouwt per concept de `OverlayConceptInput` (subtype-bewuste
 * effectieve type-ids via `RegistryRuntime`, plus de relaties én de children
 * die 't concept raken) en levert een goedkope evaluator-functie voor de
 * node-loop.
 *
 * Zero-overhead wanneer de overlay uit staat: `makeOverlayEvaluator` returnt
 * dan meteen een no-op zonder de indices op te bouwen.
 */
import {
  type Concept,
  evaluateOverlay,
  type OverlayChildInput,
  type OverlayEffect,
  type OverlayRelationInput,
  type OverlayRuleSet,
  type Relation,
} from "@ae/domain";
import { getRegistry } from "../../registry";

export interface OverlayEvalConcept {
  id: string;
  typeId: string;
  layer: string;
  stereotypes?: readonly string[];
  attributes?: Record<string, unknown>;
  name?: unknown;
}

export type OverlayEvaluator = (concept: OverlayEvalConcept) => OverlayEffect | null;

const NOOP: OverlayEvaluator = () => null;

export function makeOverlayEvaluator(
  ruleset: OverlayRuleSet | null,
  concepts: readonly Concept[],
  relations: readonly Relation[],
  /**
   * Extra parent→child-edges uit de viewState-nesting van het actieve diagram
   * (`containerId`). Nodig omdat visueel-geneste Attribuut/Identifier-rijen
   * niet altijd een model-`parent_id` hebben (bv. na AMEFF-import zonder de
   * "Koppel attributen"-backfill) — de compartiment-rijen komen namelijk uit
   * de viewState-nesting. `[parentConceptId, childConceptId]`.
   */
  viewChildEdges: readonly (readonly [string, string])[] = [],
): OverlayEvaluator {
  if (!ruleset?.enabled || ruleset.rules.every((r) => r.enabled === false)) {
    return NOOP;
  }
  const reg = getRegistry();
  const effCache = new Map<string, readonly string[]>();
  const effective = (typeId: string | undefined): readonly string[] => {
    if (!typeId) return [];
    const hit = effCache.get(typeId);
    if (hit) return hit;
    const arr = [...reg.effectiveTypeIdsFor(typeId)];
    effCache.set(typeId, arr);
    return arr;
  };
  const byId = new Map(concepts.map((c) => [c.id, c]));

  // Index alle relaties per concept-id (beide richtingen).
  const relByConcept = new Map<string, OverlayRelationInput[]>();
  const pushRel = (conceptId: string, rel: OverlayRelationInput) => {
    const list = relByConcept.get(conceptId);
    if (list) list.push(rel);
    else relByConcept.set(conceptId, [rel]);
  };
  for (const r of relations) {
    pushRel(r.sourceConceptId, {
      relationTypeId: r.typeId,
      direction: "outgoing",
      otherEffectiveTypeIds: effective(byId.get(r.targetConceptId)?.typeId),
      otherName: byId.get(r.targetConceptId)?.name,
    });
    pushRel(r.targetConceptId, {
      relationTypeId: r.typeId,
      direction: "incoming",
      otherEffectiveTypeIds: effective(byId.get(r.sourceConceptId)?.typeId),
      otherName: byId.get(r.sourceConceptId)?.name,
    });
  }

  // Index directe children per parent-concept-id: model-ownership (`parent_id`)
  // UNION visuele nesting (viewState-`containerId`), gededupliceerd.
  const childrenByParent = new Map<string, OverlayChildInput[]>();
  const seen = new Set<string>();
  const addChild = (parentConceptId: string, child: Concept) => {
    const key = `${parentConceptId} ${child.id}`;
    if (seen.has(key)) return;
    seen.add(key);
    const input: OverlayChildInput = {
      effectiveTypeIds: effective(child.typeId),
      stereotypes: child.stereotypes ?? [],
      attributes: child.attributes ?? {},
    };
    const list = childrenByParent.get(parentConceptId);
    if (list) list.push(input);
    else childrenByParent.set(parentConceptId, [input]);
  };
  for (const c of concepts) {
    if (c.parentId) addChild(c.parentId, c);
  }
  for (const [parentConceptId, childConceptId] of viewChildEdges) {
    const child = byId.get(childConceptId);
    if (child) addChild(parentConceptId, child);
  }

  return (concept) =>
    evaluateOverlay(
      {
        typeId: concept.typeId,
        effectiveTypeIds: effective(concept.typeId),
        layer: concept.layer,
        stereotypes: concept.stereotypes ?? [],
        attributes: concept.attributes ?? {},
        name: concept.name,
        relations: relByConcept.get(concept.id) ?? [],
        children: childrenByParent.get(concept.id) ?? [],
      },
      ruleset,
    );
}
